// Generated from scripts/custom-banner.ts. Rebuild with pnpm scripts:build.
"use strict";
/**
 * The <wontio-custom-banner> element of a custom banner section of the overlay group (sections/custom-banner.liquid),
 * a deferred classic script each section loads (no imports or exports, block scope, defined once). It runs when the
 * page has parsed, so every slot of the page exists. The section renders its banner into
 * an inert <template>; the element places a copy into every slot of its place (`.banner-slot[data-placement]`,
 * snippets/banner-slot.liquid), after the slot's own banners, and shows the slot.
 *
 * - Pages: the section marks data-on-page when the page is one it shows on; without it nothing is placed.
 * - Late slots: slots that arrive after the page (the cart drawer after a cart change, product recommendations,
 *   instant search results) receive the banner when they are added to the document.
 * - Product data: a banner with product shortcodes (data-product-bound) placed in a slot of another product (a product
 *   card, data-product-url) renders again for that product through the Section Rendering API when the slot comes
 *   within 200 px of the screen. One request per product and section, cached for the page.
 * - Theme editor: the section is replaced when it changes; the old element removes its copies and the new one places
 *   fresh ones. When the place is not on the page, the element shows the section's note instead.
 */
{
    const SLOT = '.banner-slot[data-placement]';
    const COPY = '[data-custom-banner]';
    const sources = new Set();
    const responses = new Map();
    let slotObserver = null;
    const fetchFor = (url, sectionId) => {
        const key = `${sectionId} ${url}`;
        const cached = responses.get(key);
        if (cached)
            return cached;
        const request = new URL(url, window.location.origin);
        request.searchParams.set('section_id', sectionId);
        const response = fetch(request.toString(), { headers: { Accept: 'text/html' } })
            .then((reply) => (reply.ok ? reply.text() : ''))
            .then((html) => {
            if (!html)
                return null;
            const document_ = new DOMParser().parseFromString(html, 'text/html');
            const template = document_.querySelector('wontio-custom-banner template');
            return template ? template.content : null;
        })
            .catch(() => null);
        responses.set(key, response);
        return response;
    };
    const show = (slot) => {
        if (slot.querySelector(':scope > *'))
            slot.hidden = false;
    };
    const hideIfEmpty = (slot) => {
        if (!slot.querySelector(':scope > *'))
            slot.hidden = true;
    };
    const placeEverywhere = () => {
        for (const source of sources)
            source.place();
    };
    const watchSlots = () => {
        if (slotObserver)
            return;
        let queued = false;
        slotObserver = new MutationObserver((records) => {
            const added = records.some((record) => Array.from(record.addedNodes).some((node) => node instanceof Element && (node.matches(SLOT) || node.querySelector(SLOT) !== null)));
            if (!added || queued)
                return;
            queued = true;
            queueMicrotask(() => {
                queued = false;
                placeEverywhere();
            });
        });
        slotObserver.observe(document.documentElement, { childList: true, subtree: true });
    };
    class CustomBanner extends HTMLElement {
        #nearObserver = null;
        #waiting = new WeakSet();
        /** The slots waiting for their product's render, by the element observed for them. */
        #hosted = new Map();
        get #sectionId() {
            return this.dataset.sectionId ?? '';
        }
        get #template() {
            return this.querySelector(':scope > template');
        }
        connectedCallback() {
            sources.add(this);
            watchSlots();
            this.place();
        }
        disconnectedCallback() {
            sources.delete(this);
            this.#nearObserver?.disconnect();
            this.#nearObserver = null;
            const id = this.#sectionId;
            for (const copy of document.querySelectorAll(`${COPY}[data-custom-banner="${CSS.escape(id)}"]`)) {
                const slot = copy.parentElement;
                copy.remove();
                if (slot?.matches(SLOT))
                    hideIfEmpty(slot);
            }
        }
        /** Places the banner into every slot of its place that does not hold it yet. */
        place() {
            const template = this.#template;
            const id = this.#sectionId;
            const placement = this.dataset.placement ?? '';
            const onPage = this.hasAttribute('data-on-page');
            const slots = onPage && template && placement
                ? Array.from(document.querySelectorAll(`${SLOT}[data-placement="${CSS.escape(placement)}"]`))
                : [];
            const note = this.querySelector('[data-custom-banner-note]');
            if (note)
                note.hidden = slots.length > 0;
            if (!template)
                return;
            const pageProduct = this.dataset.productUrl ?? '';
            const productBound = this.hasAttribute('data-product-bound');
            for (const slot of slots) {
                if (slot.querySelector(`:scope > [data-custom-banner="${CSS.escape(id)}"]`))
                    continue;
                const slotProduct = slot.dataset.productUrl ?? '';
                if (productBound && slotProduct && slotProduct !== pageProduct) {
                    this.#placeWhenNear(slot);
                    continue;
                }
                slot.append(template.content.cloneNode(true));
                show(slot);
            }
        }
        #placeWhenNear(slot) {
            if (this.#waiting.has(slot))
                return;
            this.#waiting.add(slot);
            // A hidden slot has no box to intersect, so the slot's parent stands in for it.
            const host = slot.parentElement ?? slot;
            const waiting = this.#hosted.get(host);
            if (waiting) {
                waiting.add(slot);
                return;
            }
            this.#hosted.set(host, new Set([slot]));
            this.#nearObserver ??= new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting)
                        continue;
                    this.#nearObserver?.unobserve(entry.target);
                    for (const waitingSlot of this.#hosted.get(entry.target) ?? [])
                        void this.#placeFor(waitingSlot);
                    this.#hosted.delete(entry.target);
                }
            }, { rootMargin: '200px' });
            this.#nearObserver.observe(host);
        }
        async #placeFor(slot) {
            const id = this.#sectionId;
            const fragment = await fetchFor(slot.dataset.productUrl ?? '', id);
            if (!fragment || !this.isConnected || !slot.isConnected)
                return;
            if (slot.querySelector(`:scope > [data-custom-banner="${CSS.escape(id)}"]`))
                return;
            slot.append(fragment.cloneNode(true));
            show(slot);
        }
    }
    if (!customElements.get('wontio-custom-banner'))
        customElements.define('wontio-custom-banner', CustomBanner);
}
